import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronDown, RefreshCw, Archive, Flag, Star, MessageSquare, X } from 'lucide-react';
import { useThreadStore, useAuthStore, useUIStore } from '@/store';
import { getThreads } from '@/api';
import { formatDistanceToNow } from 'date-fns';
import { ThreadItem } from '@/components/thread/ThreadItem';
import { useToast } from '@/components/ui/Toaster';
import clsx from 'clsx';

const statusOptions = ['', 'new', 'assigned', 'in_progress', 'replied', 'closed', 'spam'] as const;
const priorityOptions = ['', 'high', 'medium', 'low'] as const;

const defaultFilters = {
  status: '',
  domain: '',
  category: '',
  priority: '',
  assignee: '',
};

export default function ThreadList() {
  const navigate = useNavigate();
  const { threads, loading, filters, setFilters, setLoading, setError, setThreads, setPagination, pagination } = useThreadStore();
  const { user } = useAuthStore();
  const { rightPanelOpen, setRightPanelTab, setSidebarOpen } = useUIStore();
  const { addToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getThreads({ ...filters, limit: pagination.limit, offset: (pagination.page - 1) * pagination.limit });
      if (result.ok && result.data) {
        setThreads(result.data);
      } else {
        setError(result.error || 'Failed to load threads');
        addToast(result.error || 'Failed to load threads', 'error');
      }
    } catch (e) {
      setError('Network error');
      addToast('Network error', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, setLoading, setError, setThreads, addToast]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters({ [key]: value });
    setPagination({ page: 1 });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search would be implemented as a filter on the API side
    addToast('Search coming soon', 'info');
  };

  const domainColors: Record<string, string> = {
    'chiefops.co.za': 'bg-blue-100 text-blue-700',
    'goodaiglobal.org': 'bg-purple-100 text-purple-700',
    'kekelebaka.com': 'bg-orange-100 text-orange-700',
  };

  const statusColors: Record<string, string> = {
    new: 'bg-signal-100 text-signal-700',
    assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    replied: 'bg-purple-100 text-purple-700',
    closed: 'bg-gray-100 text-gray-700',
    spam: 'bg-red-100 text-red-700',
  };

  const priorityColors: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 sticky top-16 z-20">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-signal-500"
              aria-label="Search emails"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Status filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-signal-500"
              aria-label="Filter by status"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status ? status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ') : 'All Statuses'}
                </option>
              ))}
            </select>

            {/* Domain filter */}
            <select
              value={filters.domain}
              onChange={(e) => handleFilterChange('domain', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-signal-500"
              aria-label="Filter by domain"
            >
              <option value="">All Domains</option>
              {user?.domains?.map((domain) => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>

            {/* Priority filter */}
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-signal-500"
              aria-label="Filter by priority"
            >
              {priorityOptions.map((p) => (
                <option key={p} value={p}>
                  {p ? p.charAt(0).toUpperCase() + p.slice(1) : 'All Priorities'}
                </option>
              ))}
            </select>

            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="btn-secondary"
              aria-expanded={filterOpen}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </button>

            <button onClick={fetchThreads} disabled={loading} className="btn-secondary" aria-label="Refresh">
              <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
            </button>
          </div>
        </form>

        {/* Advanced filters */}
        {filterOpen && (
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex flex-wrap gap-3">
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                aria-label="Filter by category"
              >
                <option value="">All Categories</option>
                <option value="urgent">Urgent</option>
                <option value="partnership">Partnership</option>
                <option value="support">Support</option>
                <option value="sales">Sales</option>
                <option value="press">Press</option>
                <option value="recruiting">Recruiting</option>
                <option value="spam">Spam</option>
                <option value="newsletter">Newsletter</option>
                <option value="internal">Internal</option>
                <option value="other">Other</option>
              </select>

              <select
                value={filters.assignee}
                onChange={(e) => handleFilterChange('assignee', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                aria-label="Filter by assignee"
              >
                <option value="">All Assignees</option>
                <option value="me">Me</option>
                <option value="unassigned">Unassigned</option>
              </select>

              <button onClick={() => { setFilters({ ...defaultFilters }); setPagination({ page: 1 }); setFilterOpen(false); }} className="btn-secondary text-sm">
                <X className="w-4 h-4" />
                Clear All
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Thread List */}
        <div className="flex-1 overflow-auto">
          {loading && threads.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-signal-600 border-t-transparent" />
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <MessageSquare className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No threads found</p>
              <p className="text-sm">Adjust your filters or wait for new emails</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200" role="list" aria-label="Email threads">
              {threads.map((thread) => (
                <ThreadItem
                  key={thread.thread_id}
                  thread={thread}
                  domainColor={domainColors[thread.domain] || 'bg-gray-100 text-gray-700'}
                  statusColor={statusColors[thread.status] || 'bg-gray-100 text-gray-700'}
                  priorityColor={priorityColors[thread.priority] || 'bg-gray-100 text-gray-700'}
                  onClick={() => {
                    navigate(`/inbox/${thread.thread_id}`);
                    setRightPanelTab('details');
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {threads.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {pagination.page}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination({ page: Math.max(1, pagination.page - 1) })}
                  disabled={pagination.page === 1}
                  className="btn-secondary text-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination({ page: pagination.page + 1 })}
                  disabled={threads.length < pagination.limit}
                  className="btn-secondary text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
}